import React from 'react';
import {AuthorModel} from "../../models/author.model";
import {Grid} from "@material-ui/core";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import {ExternalLinkTypeEnum} from "../../models/external-link-type.enum";
import {StyledButton} from "../ui-base/button";
import {useHistory} from "react-router-dom";
import emptyLogo from "../../img/empty-publisher-logo.png";
import {DetailsPropWrapper} from "../ui-base/details-prop-wrapper";
import {DetailsPropCustomValueWrapper} from "../ui-base/details-prop-custom-value-wrapper";
import {SocialMediaIcons} from "../ui-base/social-media-icons";
import {CustomEditIcon} from "../ui-base/icons/custom-edit-icon";
import {CustomBookBookmarkIcon} from "../ui-base/icons/custom-book-bookmark-icon";
import {useTranslation} from "react-i18next";
import {CountryModel} from "../../models/country.model";
import {RootState} from "../../store/root-reducer";
import {useSelector} from "react-redux";


export const AuthorDetails: React.FC<{author: AuthorModel}> = ({author}) => {
    const history = useHistory();
    const { t } = useTranslation(['author', 'common']);

    const countries: CountryModel[] = useSelector(
        (state: RootState) => state.countries.countries
    );

    const getCountry = (country: string) => {
        if (country) {
            return countries.find((tmpCountry) => tmpCountry.iso === country)?.name || '';
        } else {
            return '';
        }
    }

    const getExternalLinkKey = (name: string) => {
        const linkTypes: string[] = Object.keys(ExternalLinkTypeEnum);

        return linkTypes.find((linkType) => (
            ExternalLinkTypeEnum[linkType] === name
        ));
    }

    return (
        <Grid container direction={"column"} spacing={2}>
            <Grid item>
                <StyledEditButton type={"button"} onClick={() => {
                    history.push('/author/edit-profile');
                }}
                    style={{position: 'absolute', top: '1rem', right: '1rem'}}
                >
                    <CustomEditIcon style={{fontSize: '1.1rem', marginRight: '10px'}}/> {t('common:editProfile')}
                </StyledEditButton>
            </Grid>

            <Grid item container direction={"column"} alignItems={"center"} spacing={1}>
                <Grid item>
                    <StyledAuthorLogo src={author?.photo?.file || emptyLogo}/>
                </Grid>

                <Grid item>
                    <StyledH6>{author?.first_name} {author?.last_name}</StyledH6>
                </Grid>

                <Grid item container direction={"row"} justifyContent={"center"}>
                    {author?.birth_year &&
                    <>
                        <Grid item>
                            <StyledSpan1>{new Date().getFullYear() - author?.birth_year}</StyledSpan1>
                        </Grid>
                        <Grid item style={{display: 'flex'}}>
                            <StyledDot/>
                        </Grid>
                    </>
                    }

                    <Grid item>
                        <StyledSpan1>{author?.residence_place}</StyledSpan1>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item container direction={"row"} spacing={2}>
                <Grid xs={12} sm={author?.previous_books?.length > 0 ? 4 : 5} md={3} item container direction={"column"} spacing={3}>
                    <DetailsPropWrapper header={t('profile.birthYear') + ':'} text={author?.birth_year?.toString()}/>
                    <DetailsPropWrapper header={t('profile.residencePlace') + ':'} text={author?.residence_place}/>
                    <DetailsPropWrapper header={t('profile.country') + ':'} text={getCountry(author?.country) || '-' }/>
                    <DetailsPropWrapper header={t('profile.phone') + ':'} text={author?.phone}/>
                    <DetailsPropWrapper header={t('profile.email') + ':'} text={author?.email}/>
                    <DetailsPropCustomValueWrapper header={t('profile.socialMedia') + ':'}>
                        <Grid item container direction={"row"} spacing={2}>
                            {author?.external_links.map((externalLink) => (
                                <Grid item key={externalLink.id}>
                                    <a href={externalLink.url} target="_blank" rel="noreferrer">
                                        <SocialMediaIcons iconName={getExternalLinkKey(externalLink.name)}/>
                                    </a>
                                </Grid>
                            ))}
                        </Grid>
                    </DetailsPropCustomValueWrapper>
                </Grid>

                {author?.previous_books?.length > 0 &&
                    <Grid item xs={12} sm={4} md={4} spacing={3} container direction={"column"}>
                        <DetailsPropCustomValueWrapper header={t('profile.publications') + ':'}>
                            <Grid item container direction={"column"} spacing={2}>
                                {author?.previous_books.map((previousBook) => (
                                    <Grid item container direction={"row"} key={previousBook.id} spacing={1}>
                                        <Grid item>
                                            <CustomBookBookmarkIcon style={{fill: 'transparent', fontSize: '1.3rem'}}/>
                                        </Grid>

                                        <Grid item xs container direction={"column"}>
                                            <StyledBookTitle>{previousBook.name}</StyledBookTitle>
                                            <StyledBookISBN>{previousBook.isbn}</StyledBookISBN>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </DetailsPropCustomValueWrapper>
                    </Grid>
                }

                <Grid xs={12} sm={author?.previous_books?.length > 0 ? 4 : 7} md={author?.previous_books?.length > 0 ? 5 : 9} item container direction={"column"} spacing={3}>
                    <DetailsPropWrapper header={t('profile.aboutAuthor') + ':'} text={author?.bio}/>

                    <DetailsPropCustomValueWrapper header={t('profile.awards') + ':'}>
                        <Grid item container direction={"column"} spacing={2}>
                            {author?.author_prizes?.map((prize) => (
                                <StyledBookTitle key={prize.id}>{prize.name}</StyledBookTitle>
                            ))}
                        </Grid>
                    </DetailsPropCustomValueWrapper>
                </Grid>
            </Grid>
        </Grid>
    );
}

const StyledEditButton = styled(StyledButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledAuthorLogo = styled('img')`
  height: 150px;
  border-radius: 10px;
`;

const StyledH6 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.darkGray};
  line-height: 0.9rem;
`;

const StyledDot = styled('div')`
  background-color: ${Colors.grayText};
  width: 4px;
  height: 4px;
  margin: 0 5px;
  border-radius: 50%;
  align-self: center;
`;

const StyledBookTitle = styled('p')`
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 2px;
  margin-bottom: 5px;
`;

const StyledBookISBN = styled('p')`
  font-size: 0.9rem;
`;
